// Cloudflare Turnstile (CAPTCHA) for sign-up. The Olene pre sign-up guard verifies the token
// server-side (olene-platform/infra/cdk/lib/signup-guard-stack.ts, security plan S15).
// Renders nothing when REACT_APP_TURNSTILE_SITE_KEY isn't set, so it's off until configured.
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  let s = document.querySelector(`script[src="${SRC}"]`);
  if (!s) {
    s = document.createElement("script");
    s.src = SRC;
    s.async = true;
    document.head.appendChild(s);
  }
  return new Promise((resolve, reject) => {
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", () => reject(new Error("captcha failed to load")), { once: true });
  });
}

/** onToken(token | "") whenever the challenge is solved, expires or errors. ref.reset() for a retry. */
export const Turnstile = forwardRef(function Turnstile({ onToken }, ref) {
  const box = useRef(null);
  const widget = useRef(null);
  useImperativeHandle(ref, () => ({
    reset: () => { if (window.turnstile && widget.current !== null) window.turnstile.reset(widget.current); onToken(""); },
  }));
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !box.current) return;
      widget.current = window.turnstile.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t) => onToken(t),
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
      });
    }).catch(() => onToken(""));
    return () => {
      cancelled = true;
      if (window.turnstile && widget.current !== null) window.turnstile.remove(widget.current);
    };
  }, [onToken]);
  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={box} className="mb-4" />;
});
